import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { EshavaConfirmDialog, EshavaToastHost, TranslatePipe } from 'eshava-shared';

import { HelpFlyout } from './help-flyout';
import { SecretDialog } from '@portal/shared/ui/secret/secret-dialog';
import { HelpService } from '@portal/core/help/help.service';
import { PermissionsService } from '@portal/core/auth/permissions.service';
import { allowedNavigation } from './navigation';
import { SideNav } from './side-nav';
import { TopBar } from './top-bar';

/**
 * The one layout there is. No branching between a public and a private frame -- nothing in the
 * portal is reachable without a token, so there is no anonymous state to lay out.
 *
 * The help flyout is part of the grid rather than an overlay: on wide screens it takes a column of
 * its own and pushes the content aside, in the compact class it covers it. Either way it does not
 * trap focus and does not lock scrolling, because help is read while working.
 */
@Component({
  selector: 'portal-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EshavaToastHost, EshavaConfirmDialog, RouterOutlet, SecretDialog, TopBar, SideNav, HelpFlyout, TranslatePipe],
  template: `
    <a class="shell__skip" href="#content">{{ 'shell.skipToContent' | t }}</a>

    <portal-top-bar class="shell__top" />

    <portal-side-nav class="shell__side" [entries]="navigation()" />

    <main id="content" class="shell__content" tabindex="-1">
      <router-outlet />
    </main>

    @if (help.isOpen()) {
      <portal-help-flyout class="shell__help" />
    }

    <!-- Rendered once for the whole application. It is a top-layer dialog, so where it sits in the
         markup does not matter -- and one instance means one blocking question at a time. -->
    <eshava-confirm-dialog />

    <!-- Also once for the whole application, and for the same reason. -->
    <portal-secret-dialog />

    <!--
      Where a failed load says so. The shared table raises a toast when its loader rejects, and
      without a host that message goes nowhere -- a list would sit empty and look like a list with no
      rows. One instance, like the two above.
    -->
    <eshava-toast-host [dismissLabel]="'common.dismiss' | t" />
  `,
  styles: `
    :host {
      display: grid;
      grid-template-areas:
        'top top top'
        'side content help';
      grid-template-columns: auto 1fr auto;
      grid-template-rows: var(--topbar-height) 1fr;
      height: 100vh;
      overflow: hidden;
    }

    .shell__top {
      grid-area: top;
    }

    .shell__side {
      grid-area: side;
      overflow-y: auto;
    }

    .shell__content {
      grid-area: content;
      padding: var(--space-5);
      overflow: auto;
    }

    .shell__help {
      grid-area: help;
      overflow: hidden;
    }

    /* Compact class: the flyout covers the content instead of taking a column, otherwise nothing of
       the content would be left at 1024px. */
    @media (max-width: 1439px) {
      :host {
        grid-template-columns: auto 1fr 0;
      }

      .shell__help {
        position: fixed;
        top: var(--topbar-height);
        right: 0;
        bottom: 0;
        z-index: var(--z-flyout);
        box-shadow: var(--shadow-card);
      }
    }

    /* Visible only when focused, which is the point: the first tab stop on the page skips the
       navigation. */
    .shell__skip {
      position: absolute;
      top: var(--space-2);
      left: var(--space-2);
      z-index: var(--z-toast);
      padding: var(--space-2) var(--space-3);
      color: var(--on-accent);
      background-color: var(--accent);
      border-radius: var(--radius-sm);
      transform: translateY(-200%);
      transition: transform var(--duration) var(--ease);
    }

    .shell__skip:focus {
      transform: translateY(0);
      text-decoration: none;
    }
  `
})
export class Shell {
  private readonly permissions = inject(PermissionsService);

  /**
   * The areas, handed to the sidebar. The list is the portal's; the rendering is not.
   *
   * **Filtered here rather than in the sidebar**, which renders what it is given and knows nothing
   * about permissions -- the same reason it takes the list as an input at all.
   */
  protected readonly navigation = computed(() =>
    allowedNavigation(required => this.permissions.hasAll(required)));

  protected readonly help = inject(HelpService);
}